import React, { useState, useEffect, useCallback } from 'react';
import PropTypes from 'prop-types';
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button,
    Box,
    CircularProgress,
    Alert,
    IconButton,
    TextField,
    Checkbox,
    Tooltip
} from '@mui/material';
import {
    DataGrid,
    GridToolbarContainer,
    GridActionsCellItem,
    GridRowModes,
    GridRowEditStopReasons,
} from '@mui/x-data-grid';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/DeleteOutline';
import SaveIcon from '@mui/icons-material/Save';
import CancelIcon from '@mui/icons-material/Close';
import SettingsIcon from '@mui/icons-material/Settings';

import {
    useListChatGroupTags,
    useCreateChatGroupTag,
    useUpdateChatGroupTag,
    useDeleteChatGroupTag
} from '../services/appService';
import ConfirmDialog from './ConfirmDialog';

// Reusable EditToolbar component (can be extracted to a separate file if needed)
function EditToolbar(props) {
    const { setRows, setRowModesModel, noun } = props;

    const handleClick = () => {
        const id = Date.now(); // Temporary ID for new row
        setRows((oldRows) => [
            ...oldRows,
            {
                id, name: '', display_order: 0, is_admin_only: false, is_highlighted: false, isNew: true
            },
        ]);
        setRowModesModel((oldModel) => ({
            ...oldModel,
            [id]: { mode: GridRowModes.Edit, fieldToFocus: 'name' },
        }));
    };

    return (
        <GridToolbarContainer>
            <Button color="primary" startIcon={<AddIcon />} onClick={handleClick}>
                Add {noun}
            </Button>
        </GridToolbarContainer>
    );
}

EditToolbar.propTypes = {
    setRowModesModel: PropTypes.func.isRequired,
    setRows: PropTypes.func.isRequired,
    noun: PropTypes.string.isRequired,
};

function ChatTagsConfigModal({ open, onClose, appId }) {
    const [rows, setRows] = useState([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');
    const [rowModesModel, setRowModesModel] = useState({});
    const [confirmDialogOpen, setConfirmDialogOpen] = useState(false);
    const [itemToDelete, setItemToDelete] = useState(null);

    const listTagsHook = useListChatGroupTags();
    const createTagHook = useCreateChatGroupTag();
    const updateTagHook = useUpdateChatGroupTag();
    const deleteTagHook = useDeleteChatGroupTag();

    const fetchTags = useCallback(async () => {
        if (open && appId) {
            setLoading(true);
            setError('');
            try {
                const data = await listTagsHook(appId);
                setRows(data || []);
            } catch (err) {
                console.error("Error fetching chat tags:", err);
                setError(err.message || 'Failed to load tags.');
            } finally {
                setLoading(false);
            }
        }
    }, [open, appId, listTagsHook]);

    useEffect(() => {
        fetchTags();
    }, [fetchTags]);

    const handleRowEditStop = (params, event) => {
        if (params.reason === GridRowEditStopReasons.rowFocusOut) {
            event.defaultMuiPrevented = true;
        }
    };

    const handleEditClick = (id) => () => {
        setRowModesModel({ ...rowModesModel, [id]: { mode: GridRowModes.Edit } });
    };

    const handleSaveClick = (id) => () => {
        setRowModesModel({ ...rowModesModel, [id]: { mode: GridRowModes.View } });
    };

    const handleDeleteClick = (id) => () => {
        setItemToDelete(id);
        setConfirmDialogOpen(true);
    };

    const handleConfirmDelete = async () => {
        if (!itemToDelete) return;
        setLoading(true);
        setError('');
        try {
            await deleteTagHook(appId, itemToDelete);
            setRows(rows.filter((row) => row.id !== itemToDelete));
            fetchTags(); // Re-fetch
        } catch (err) {
            console.error("Error deleting tag:", err);
            setError(err.message || 'Failed to delete tag.');
            setLoading(false);
        } finally {
            setConfirmDialogOpen(false);
            setItemToDelete(null);
        }
    };

    const handleCancelClick = (id) => () => {
        setRowModesModel({
            ...rowModesModel,
            [id]: { mode: GridRowModes.View, ignoreModifications: true },
        });

        const editedRow = rows.find((row) => row.id === id);
        if (editedRow?.isNew) {
            setRows(rows.filter((row) => row.id !== id));
        }
    };

    const processRowUpdate = async (newRow) => {
        setLoading(true);
        setError('');
        const updatedRow = { ...newRow, isNew: false };
        try {
            let savedRow;
            if (newRow.isNew) {
                const { id, ...dataToCreate } = updatedRow;
                savedRow = await createTagHook(appId, dataToCreate);
                setRows(rows.map((row) => (row.id === newRow.id ? { ...savedRow, id: savedRow.id } : row)));
            } else {
                savedRow = await updateTagHook(appId, newRow.id, updatedRow);
                setRows(rows.map((row) => (row.id === newRow.id ? savedRow : row)));
            }
            fetchTags(); // Re-fetch
            return savedRow;
        } catch (err) {
            console.error("Error saving tag:", err);
            setError(err.message || 'Failed to save tag.');
            setLoading(false);
             const oldRow = rows.find(r => r.id === newRow.id);
             return oldRow ?? newRow;
        }
    };

    const handleRowModesModelChange = (newRowModesModel) => {
        setRowModesModel(newRowModesModel);
    };

    const handleProcessRowUpdateError = useCallback((error) => {
        console.error("Error processing row update:", error);
        setError(error.message || 'An error occurred while saving.');
    }, []);

    const columns = [
        { field: 'name', headerName: 'Tag Name', width: 200, editable: true },
        {
            field: 'display_order',
            headerName: 'Order',
            type: 'number',
            width: 90,
            align: 'left',
            headerAlign: 'left',
            editable: true,
        },
        {
            field: 'is_admin_only',
            headerName: 'Admin Only?',
            type: 'boolean',
            width: 120,
            editable: true,
        },
        {
            field: 'is_highlighted',
            headerName: 'Highlight?',
            type: 'boolean',
            width: 120,
            editable: true,
        },
        {
            field: 'actions',
            type: 'actions',
            headerName: 'Actions',
            width: 100,
            cellClassName: 'actions',
            getActions: ({ id }) => {
                const isInEditMode = rowModesModel[id]?.mode === GridRowModes.Edit;
                if (isInEditMode) {
                    return [
                        <GridActionsCellItem icon={<SaveIcon />} label="Save" sx={{ color: 'primary.main' }} onClick={handleSaveClick(id)} />,
                        <GridActionsCellItem icon={<CancelIcon />} label="Cancel" className="textPrimary" onClick={handleCancelClick(id)} color="inherit" />,
                    ];
                }
                return [
                    <GridActionsCellItem icon={<EditIcon />} label="Edit" className="textPrimary" onClick={handleEditClick(id)} color="inherit" />,
                    <GridActionsCellItem icon={<DeleteIcon />} label="Delete" onClick={handleDeleteClick(id)} color="inherit" />,
                ];
            },
        },
    ];

    return (
        <Dialog open={open} onClose={onClose} maxWidth="lg" fullWidth>
            <DialogTitle>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <SettingsIcon />
                    Manage Chat Group Tags
                 </Box>
            </DialogTitle>
            <DialogContent>
                {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
                <Box sx={{ height: 500, width: '100%' }}>
                    <DataGrid
                        rows={rows}
                        columns={columns}
                        editMode="row"
                        rowModesModel={rowModesModel}
                        onRowModesModelChange={handleRowModesModelChange}
                        onRowEditStop={handleRowEditStop}
                        processRowUpdate={processRowUpdate}
                        onProcessRowUpdateError={handleProcessRowUpdateError}
                        slots={{ toolbar: EditToolbar }}
                        slotProps={{
                            toolbar: { setRows, setRowModesModel, noun: 'Tag' },
                        }}
                        loading={loading}
                        initialState={{
                            sorting: {
                                sortModel: [{ field: 'display_order', sort: 'asc' }],
                            },
                        }}
                    />
                </Box>
            </DialogContent>
            <DialogActions>
                <Button onClick={onClose}>Close</Button>
            </DialogActions>

            <ConfirmDialog
                open={confirmDialogOpen}
                onClose={() => setConfirmDialogOpen(false)}
                onConfirm={handleConfirmDelete}
                title="Delete Tag?"
                message={`Are you sure you want to delete this chat tag? This might affect existing chat groups using it.`}
            />
        </Dialog>
    );
}

ChatTagsConfigModal.propTypes = {
    open: PropTypes.bool.isRequired,
    onClose: PropTypes.func.isRequired,
    appId: PropTypes.string.isRequired,
};

export default ChatTagsConfigModal; 